import React, { useState } from "react";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { createChallenge } from "api/student/v2/challenge.api";
import { IChallenge, IChallengeCreateRequest } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";

interface ChallengeCreatePopupProps {
    onSuccess: (challenge: IChallenge) => void;
}

const ChallengeCreatePopup: React.FC<ChallengeCreatePopupProps> = ({
    onSuccess
}) => {
    const { hidePopup } = usePopup();
    const [title, setTitle] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim()) {
            setError("챌린지 제목을 입력해주세요.");
            return;
        }

        if (title.length < 2) {
            setError("챌린지 제목은 2자 이상 입력해주세요.");
            return;
        }

        if (title.length > 100) {
            setError("챌린지 제목은 100자 이하로 입력해주세요.");
            return;
        }

        try {
            setIsSubmitting(true);
            setError("");

            const challengeData: IChallengeCreateRequest = {
                title: title.trim()
            };

            const response = await createChallenge(challengeData);

            // 성공 시 콜백 호출
            onSuccess(response.challenge);

            // 폼 초기화
            setTitle("");

            // 팝업 닫기
            hidePopup(0); // 가장 최근 팝업 닫기
        } catch (error) {
            console.error("챌린지 생성 실패:", error);
            setError("챌린지 생성에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setTitle("");
            setError("");
            hidePopup(0); // 가장 최근 팝업 닫기
        }
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
        if (error) {
            setError("");
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4
            !m-0">

            {/* 팝업 컨테이너 */}
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                <form onSubmit={handleSubmit}>
                    {/* 헤더 */}
                    <div className="bg-white px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">
                                챌린지 등록
                            </h3>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* 본문 */}
                    <div className="bg-white px-6 py-4">
                        <div className="space-y-4">
                            {/* 제목 입력 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    챌린지 제목 *
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={handleTitleChange}
                                    placeholder="챌린지 제목을 입력하세요"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent"
                                    disabled={isSubmitting}
                                    maxLength={100}
                                />
                                <div className="mt-1 flex justify-between text-xs text-gray-500">
                                    <span>{title.length}/100</span>
                                </div>
                            </div>

                            {/* 에러 메시지 */}
                            {error && (
                                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 푸터 */}
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !title.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#CF3688] rounded-lg hover:bg-[#B8327A] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    등록 중...
                                </div>
                            ) : (
                                "등록"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChallengeCreatePopup;
